import React from 'react';
import { View, Text, StyleSheet, Platform, TouchableOpacity, ActivityIndicator } from 'react-native';
import { FavouriteStarIcon } from './StandupIcons';

interface NewsCardProps {
  news_id: number;
  title: string;
  date: string;
  description: string;
  is_favourite?: boolean;
  width?: number | string;
  height?: number | string;
  handleToggleFavorite: (news_id: number) => void;
  isLoading?: boolean;
  disabled?: boolean;
  is_truncated?: boolean;
  onViewMore?: () => void;
  // New prop to hide star icon (for dashboard view where starring is not allowed)
  hideStarIcon?: boolean;
  // Position of the date: 'top' (beside star icon), 'bottom' (bottom right), or 'below-title' (under the title)
  datePosition?: 'top' | 'bottom' | 'below-title';
}

const NewsCard: React.FC<NewsCardProps> = ({ 
  news_id, 
  title, 
  date, 
  description, 
  is_favourite, 
  width, 
  height, 
  handleToggleFavorite, 
  isLoading = false, 
  disabled = false,
  is_truncated = false,
  onViewMore,
  hideStarIcon = false,
  datePosition = 'top',
}) => {
  const containerStyle: any = [styles.container];
  if (width !== undefined) containerStyle.push({ width });
  if (height !== undefined) containerStyle.push({ height });
  return (
    <View style={containerStyle}>
      {/* Header with title and optionally date (if datePosition='top') */}
      <View style={styles.header}>
        <View style={datePosition === 'below-title' ? styles.titleContainerVertical : styles.titleDateContainer}>
          <Text style={styles.title} numberOfLines={2} ellipsizeMode="tail">{title}</Text>
          {datePosition === 'top' && <Text style={styles.date}>{date}</Text>}
          {datePosition === 'below-title' && <Text style={styles.dateBelowTitle}>{date}</Text>}
        </View>
        {/* Only show star icon if not hidden (for dashboard, hideStarIcon=true) */}
        {!hideStarIcon && (
          <TouchableOpacity
            onPress={() => handleToggleFavorite(news_id)}
            disabled={disabled || isLoading}
            style={[{ opacity: disabled || isLoading ? 0.5 : 1 }]}
          >
            {isLoading ? (
              <ActivityIndicator size="small" color="#034895" />
            ) : (
              <FavouriteStarIcon filled={is_favourite} />
            )}
          </TouchableOpacity>
        )}
      </View>

      {/* Description */}
      <Text style={styles.description}>{description}</Text>
      
      {/* Footer section - View more button and/or date at bottom */}
      {(is_truncated && onViewMore) || datePosition === 'bottom' ? (
        <View style={styles.footerContainer}>
          {is_truncated && onViewMore ? (
            <TouchableOpacity style={styles.viewMoreButton} onPress={onViewMore} activeOpacity={0.7}>
              <Text style={styles.viewMoreText}>View more</Text>
            </TouchableOpacity>
          ) : (
            <View />
          )}
          {datePosition === 'bottom' && <Text style={styles.dateBottom}>{date}</Text>}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderColor: '#E2E8F0',
    borderWidth: 1,
    padding: 16,
    minHeight: 136, 
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    gap: 8,
  },
  titleDateContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    flex: 1,
  },
  titleContainerVertical: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    flex: 1,
  },
  titleWrapper: {
    flex: 1,
    minWidth: 0, 
  },
  title: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: '#1C1C1C',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
    lineHeight: 18,
    flexShrink: 1, 
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? { wordBreak: 'break-word' } : {}),
  },
  date: {
    fontSize: 10,
    fontWeight: '400',
    color: '#757575',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
    flexShrink: 0, 
    marginLeft: 8,
    alignSelf: 'flex-start',
  },
  dateBelowTitle: {
    fontSize: 10,
    fontWeight: '400',
    color: '#757575',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
  description: {
    fontSize: 12,
    fontWeight: '400',
    color: '#616161',
    lineHeight: 18,
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
  viewMoreContainer: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginTop: 8,
  },
  footerContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    marginTop: 8,
  },
  viewMoreButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  viewMoreText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#005CBC',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
  dateBottom: {
    fontSize: 10,
    fontWeight: '400',
    color: '#757575',
    fontFamily: Platform.OS === 'ios' ? 'System' : 'sans-serif',
  },
});

export default NewsCard;